import { t } from '../../i18n/i18n';
import React, { useState, useEffect, useContext } from 'react';
import { APIBaseUrl } from '../../i18n/environments';
import acceptCookiesTranslations from '../../i18n/translations/components/accept-cookies/accept-cookies.json';
import Tap from '@tapfiliate/tapfiliate-js';

import './styles/accept-cookies.scss';
import { getCookie, initCookies, setCookie } from '../../utils/cookies';
import { InitGTM } from './initGoogleTagManager';
import { CookieState } from '../layout/layout';
import { CookieParagraph, CookiePreferences, CookieTitle } from './styles';
import CookieConsent from 'react-cookie-consent';
import LangA from '../../i18n/LangA';
import { useI18nInitNamespace } from '../../i18n/useI18nInitNamespace';

interface ICookiePreferences {
  necessary: boolean;
  advertising: boolean;
}

export const getCookiePreferences = (): ICookiePreferences => {
  const cookie = getCookie('cookieConsent');
  const preferences: ICookiePreferences = JSON.parse(cookie);
  return preferences;
};

const isDocsLayout = (): boolean => {
  if (typeof window !== 'undefined') {
    const urlIncludesDocs = window?.location?.href?.includes('docs/');
    return urlIncludesDocs;
  }
};

const CookiePreferencesComponent: React.FC = () => {
  useI18nInitNamespace('acceptCookies', acceptCookiesTranslations, true);
  const modalState = useContext(CookieState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUsa, setIsUsa] = useState<boolean>(false);
  const [scriptsLoaded, setScriptsLoaded] = useState<boolean>(false);
  const [cookiePreferences, setCookiePreferences] = useState<ICookiePreferences>(
    JSON.parse(getCookie('cookieConsent')) || {
      necessary: true,
      advertising: false,
    }
  );

  const setGtagConsent = (value = true) => {
    return new Promise((resolve, reject) => {
      gtag('consent', 'update', {
        ad_storage: value ? 'granted' : 'denied',
        analytics_storage: value ? 'granted' : 'denied',
        ad_user_data: value ? 'granted' : 'denied',
        ad_personalization: value ? 'granted' : 'denied',
        personalization_storage: value ? 'granted' : 'denied',
        functionality_storage: value ? 'granted' : 'denied',
        security_storage: value ? 'granted' : 'denied',
      });

      resolve();
    });
  };

  const setGTMCookies = async (value = true, retries = 10) => {
    if (typeof gtag !== 'undefined') {
      setGtagConsent(value).then(() => {
        if (typeof window.dataLayer !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          setTimeout(() => {
            window.dataLayer.push({
              event: 'cookie_consent_marketing',
              cookie_consent_statistics: value,
            });
          }, 500);
        }
      });
    } else if (retries > 0) {
      setTimeout(() => {
        setGTMCookies(value, retries - 1);
      }, 1000);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      Tap.init('8323-a50068');
      InitGTM();
      initCookies();

      const consent = getCookie('cookieConsent');

      if (consent) {
        const preferences: ICookiePreferences = JSON.parse(consent);
        setGTMCookies(preferences?.advertising);
        return setCookiePreferences(preferences);
      } else {
        const isFromEuropeOrCalifornia = await getGeolocation();
        modalState?.toggleCookieModal();

        if (!isFromEuropeOrCalifornia) {
          setGTMCookies();
        }
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!scriptsLoaded) {
      loadNecessaryCookies(cookiePreferences);
      setScriptsLoaded(true);
    }
  }, [cookiePreferences]);

  const acceptAllCookies = (): void => {
    setPreferences({ necessary: true, advertising: true });
    loadNecessaryCookies({ necessary: true, advertising: true });
    modalState?.toggleCookieModal();
    setGTMCookies();
  };

  const acceptNecessaryCookies = (): void => {
    setPreferences({ necessary: true, advertising: false });
    modalState?.toggleCookieModal();
    // setGTMCookies(false);
  };

  const saveCookiePreferences = (): void => {
    setPreferences(cookiePreferences);
    loadNecessaryCookies(cookiePreferences);
    modalState?.toggleCookieModal();
    setGTMCookies(cookiePreferences?.advertising);
  };

  const getGeolocation = async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${APIBaseUrl}/customers/geolocation/`);
      if (!response.ok) {
        throw new Error('An error occured while trying to retrieve more information.');
      }

      const data = await response.json();
      setIsUsa(!data?.allow);

      if (data?.allow) {
        setCookie('zb_non_euca', JSON.stringify('show'), 365);
      }

      setIsLoading(false);
      return data?.allow;
    } catch (error) {
      const consent = getCookie('cookieConsent');
      if (!consent) {
        modalState?.toggleCookieModal();
      }

      setIsLoading(false);
      return true;
    }
  };

  const toggleManageCookieSettings = (): void => {
    if (modalState?.hideBackButton) {
      return modalState?.toggleCookieModal();
    }

    modalState?.toggleCookieSettings();
  };

  const setPreferences = (preferences: ICookiePreferences): void => {
    setCookie('cookieConsent', JSON.stringify(preferences), 365);
    setCookiePreferences(preferences);
  };

  const loadNecessaryCookies = (preferences: ICookiePreferences): void => {
    if (preferences.necessary) {
      Tap.init('8323-a50068');
    }

    if (preferences.advertising) {
      InitGTM();
      initCookies();
    }
  };

  return isLoading ? (
    <></>
  ) : !isUsa ? (
    <React.Fragment>
      {modalState?.isCookieModalVisible && (
        <CookiePreferences className={`cookie-preferences ${modalState?.showSettingsInCookieModal ? 'expand' : ''}`}>
          {!modalState?.showSettingsInCookieModal ? (
            <>
              <div className="description">
                <div>
                  <CookieTitle>This website uses cookies!</CookieTitle>
                  <CookieParagraph>
                    We utilize strictly necessary cookies for browsing and core functionality, along with performance cookies, and targeting
                    or advertising cookies from third parties, such as social networks, to provide tailored content. By clicking "Accept"
                    you agree with the use of strictly necessary cookies (which are essential to your ability to use the Site); performance
                    cookies (which are used to gain insight on website usage and which are used to improve our site and services); and
                    targeting or advertising cookies (both from ZeroBounce and other trusted partners).
                  </CookieParagraph>
                  <a
                    href="#"
                    style={{ color: '#197bac' }}
                    onClick={(e) => {
                      e.preventDefault();
                      modalState?.toggleCookieSettings();
                    }}
                  >
                    Manage Cookie Preferences
                  </a>
                </div>
              </div>
              <div className="buttons-container">
                <button onClick={() => acceptAllCookies()} className="zero-button">
                  Accept
                </button>
                <button onClick={() => acceptNecessaryCookies()} className="zero-button grey-button">
                  Decline
                </button>
              </div>
            </>
          ) : (
            <div className="manage-cookies-container">
              <div className="title">Manage Cookie Settings</div>
              <p className="mb-3">
                Adjust your cookie preferences for our site. <br /> Strictly necessary cookies are mandatory and cannot be disabled.
              </p>
              <form
                onSubmit={() => {
                  saveCookiePreferences();
                }}
              >
                <div className="cookie-options">
                  <fieldset>
                    <legend className="cookie-option">Strictly Necessary Cookies</legend>
                    <div className="toggles small d-flex align-items-center">
                      <label className="t-switch disabled">
                        <input disabled type="checkbox" name="functionalityCookies" checked />
                        <span className="t-slider round"></span>
                      </label>
                      These cookies are essential for you to browse the website and use its features.
                    </div>
                  </fieldset>
                  <fieldset>
                    <legend className="cookie-option">Functionality and Advertising Cookies</legend>
                    <div className="toggles small d-flex align-items-center">
                      <label className="t-switch">
                        <input
                          type="checkbox"
                          name="functionalityCookies"
                          checked={cookiePreferences.advertising}
                          onChange={(e) =>
                            setCookiePreferences({
                              ...cookiePreferences,
                              advertising: e.target.checked,
                            })
                          }
                        />
                        <span className="t-slider round"></span>
                      </label>
                      These cookies allow us to remember choices you make and provide enhanced, more personal features.
                    </div>
                  </fieldset>
                </div>
                <div className="form-buttons-container">
                  <button type="submit" className="zero-button">
                    Save Preferences
                  </button>
                  {!modalState?.hideBackButton && (
                    <button type="button" onClick={() => toggleManageCookieSettings()} className="zero-button grey-button">
                      Back
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}
        </CookiePreferences>
      )}
    </React.Fragment>
  ) : (
    <CookieConsent
      location="bottom"
      style={{
        zIndex: 999,
        textAlign: 'center',
        background: 'rgba(23, 30, 37, 0.898039)',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      contentStyle={{ margin: '5px' }}
      containerClasses="cookies"
      buttonText={t('Accept')}
      buttonClasses="btn btn--primary"
      buttonWrapperClasses="btn--wrapper"
      buttonStyle={{ borderRadius: '7px' }}
      onAccept={() => acceptAllCookies()}
    >
      <p>
        {t('This_site')}{' '}
        <LangA href="/privacy-policy" title={t('Learn_More')}>
          {t('Learn_More1')}
          <span className="seo-hidden">{t('about_our')}</span>
        </LangA>
      </p>
    </CookieConsent>
  );
};

export default CookiePreferencesComponent;
